/**
 * Adesa Run List page — the working sheet for one weekly auction.
 * Wraps content in WholesaleAuctionProvider so the inner component can
 * call useWholesaleAuction(). Layout mirrors the Adesa Cleared List
 * sibling: header (h1 + subtitle + a controls cluster on the right),
 * File Requirements + Active Vendor Files panels stacked above a silver
 * divider, then the run-specific banner + working table once a run is
 * built. The header's primary action shifts with run state
 * (Build Run → Mark Completed → Start New Run) with the Download menu
 * beside it, disabled until a run exists — matching the Cleared List's
 * "controls live top-right" rhythm.
 */
import React, { useCallback, useEffect } from "react"
import { useSearchParams } from "react-router-dom"

import AuctionActiveVendorFilesPanel from "../../../components/wholesale-auction/AuctionActiveVendorFilesPanel"
import AuctionFileRequirementsPanel from "../../../components/wholesale-auction/AuctionFileRequirementsPanel"
import AuctionRunTable from "../../../components/wholesale-auction/AuctionRunTable"
import AuctionUnmappedRooftopBanner from "../../../components/wholesale-auction/AuctionUnmappedRooftopBanner"
import RunSwitcher from "../../../components/wholesale-auction/RunSwitcher"
import Layout from "../../../components/Layout"
import { AuctionRunSummary, AuctionSession } from "../../../api/wholesaleAuction"
import { MOBILE_FULL_BUTTON } from "../../../config/mobileStyles"
import { THEME } from "../../../config/theme"
import {
  WholesaleAuctionProvider,
  useWholesaleAuction,
} from "../../../context/WholesaleAuctionContext"
import { useDealerGroup } from "../../../context/DealerGroupContext"
import DealerGroupRequiredNotice from "../../../components/DealerGroupRequiredNotice"
import { useIsMobile } from "../../../hooks/useIsMobile"

export default function AdesaRunListPage() {
  return (
    <Layout>
      <WholesaleAuctionProvider>
        <AdesaRunListPageContent />
      </WholesaleAuctionProvider>
    </Layout>
  )
}

function AdesaRunListPageContent() {
  const {
    session,
    sessionLoading,
    sessionError,
    refreshSession,
    clearSession,
    buildRun,
    buildLoading,
    buildError,
    runDetail,
    loadRunDetail,
    resumeLatestDraft,
    clearRunDetail,
    runDetailLoading,
    runDetailError,
    completeRun,
    completeLoading,
    completeError,
    reopenRun,
    reopenLoading,
    reopenError,
    runs,
    refreshRuns,
  } = useWholesaleAuction()
  const { dealerGroupId } = useDealerGroup()
  const isMobile = useIsMobile()

  // Allow deep-linking to a specific run from the Reports page —
  // `?runId=42` loads that run on mount so completed past runs open
  // directly into the read-only view without going through Build Run.
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedRunId = searchParams.get("runId")

  useEffect(() => {
    refreshSession()
  }, [refreshSession])

  // Populate the run-switcher dropdown (run ids + status pills).
  useEffect(() => {
    refreshRuns()
  }, [refreshRuns])

  // Load a specific run when deep-linked from Reports (?runId=…); otherwise
  // resume the most recent in-progress draft so the working sheet reappears
  // on a fresh visit (e.g. built Monday, returning Wednesday) without having
  // to go through Reports.
  useEffect(() => {
    if (requestedRunId) {
      const parsed = Number(requestedRunId)
      if (!Number.isNaN(parsed)) loadRunDetail(parsed)
      return
    }
    resumeLatestDraft()
  }, [requestedRunId, loadRunDetail, resumeLatestDraft])

  const bothUploaded = !!session?.adesa_run_list && !!session?.vauto

  const handleBuildRun = useCallback(async () => {
    try {
      const newRun = await buildRun()
      await loadRunDetail(newRun.id)
    } catch {
      // Errors captured on the context as buildError / runDetailError.
    }
  }, [buildRun, loadRunDetail])

  const handleMarkCompleted = useCallback(async () => {
    if (!runDetail) return
    if (
      !window.confirm(
        "Mark this run as completed? Editable cells will lock and the run becomes read-only.",
      )
    ) {
      return
    }
    try {
      await completeRun(runDetail.run.id)
    } catch {
      // Errors captured on context as completeError.
    }
  }, [completeRun, runDetail])

  const handleStartNew = useCallback(async () => {
    if (
      !window.confirm(
        "Start a new run? The saved run stays viewable from the Reports page, and the " +
          "uploaded files are cleared so you can upload the next auction's files.",
      )
    ) {
      return
    }
    // Drop the runId query param so the loader effect doesn't re-fetch
    // the just-cleared run on the next render.
    if (searchParams.has("runId")) {
      const next = new URLSearchParams(searchParams)
      next.delete("runId")
      setSearchParams(next, { replace: true })
    }
    clearRunDetail()
    // Wipe the uploaded files so the upload slots reset for the next auction.
    try {
      await clearSession()
    } catch {
      // Surfaced on the context as fileActionError; the run view is already cleared.
    }
  }, [clearRunDetail, clearSession, searchParams, setSearchParams])

  const handleReopen = useCallback(async () => {
    if (!runDetail) return
    try {
      await reopenRun(runDetail.run.id)
    } catch {
      // Error captured on context as reopenError.
    }
  }, [reopenRun, runDetail])

  const run = runDetail?.run ?? null
  // One in-flight flag drives the buttons' disabled + label state; only
  // one action is reachable per run-status, so they never overlap.
  const busy = buildLoading || runDetailLoading || completeLoading || reopenLoading
  // Surface whichever action last errored in the header cluster, the way
  // the Cleared List surfaces its single runError.
  const actionError = completeError ?? reopenError ?? buildError ?? runDetailError

  if (dealerGroupId === null) {
    return (
      <div style={{ padding: THEME.spacing.lg, fontFamily: THEME.fonts.primary }}>
        <DealerGroupRequiredNotice surface="the Adesa run list" />
      </div>
    )
  }

  return (
    <div
      style={{
        padding: isMobile ? THEME.spacing.md : THEME.spacing.lg,
        fontFamily: THEME.fonts.primary,
      }}
    >
      <Header
        session={session}
        run={run}
        runs={runs}
        bothUploaded={bothUploaded}
        busy={busy}
        actionError={actionError}
        isMobile={isMobile}
        onSelectRun={loadRunDetail}
        onRefreshRuns={refreshRuns}
        onBuildRun={handleBuildRun}
        onMarkCompleted={handleMarkCompleted}
        onReopen={handleReopen}
        onStartNew={handleStartNew}
      />

      {sessionLoading && (
        <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
          Loading session…
        </span>
      )}
      {sessionError && (
        <span style={{ color: THEME.colors.semantic.danger }}>{sessionError}</span>
      )}

      {!sessionLoading && !sessionError && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: THEME.spacing.sm,
              marginBottom: THEME.spacing.md,
            }}
          >
            <AuctionFileRequirementsPanel session={session} runLoaded={run !== null} />
            <AuctionActiveVendorFilesPanel session={session} />
          </div>

          <div
            style={{
              borderTop: `1px solid ${THEME.colors.silver}`,
              marginBottom: THEME.spacing.md,
            }}
          />

          {runDetail && (
            <>
              <AuctionUnmappedRooftopBanner unmapped={runDetail.unmapped_rooftops} />
              <AuctionRunTable detail={runDetail} />
            </>
          )}
        </>
      )}
    </div>
  )
}

interface HeaderProps {
  session: AuctionSession | null
  run: AuctionRunSummary | null
  runs: AuctionRunSummary[]
  bothUploaded: boolean
  busy: boolean
  actionError: string | null
  isMobile: boolean
  onSelectRun: (runId: number) => void
  onRefreshRuns: () => void
  onBuildRun: () => void
  onMarkCompleted: () => void
  onReopen: () => void
  onStartNew: () => void
}

function Header({
  session,
  run,
  runs,
  bothUploaded,
  busy,
  actionError,
  isMobile,
  onSelectRun,
  onRefreshRuns,
  onBuildRun,
  onMarkCompleted,
  onReopen,
  onStartNew,
}: HeaderProps) {
  const subtitle = buildSubtitle(session, run)

  return (
    <div
      style={{
        display: "flex",
        // Stack the title above the controls on mobile so the controls cluster
        // wraps within the viewport instead of forcing horizontal scroll.
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? THEME.spacing.sm : 0,
        marginBottom: THEME.spacing.md,
      }}
    >
      <div>
        <h1
          style={{
            fontSize: isMobile ? 20 : 24,
            fontWeight: 500,
            letterSpacing: "-0.5px",
            margin: 0,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          Adesa Run List
        </h1>
        {subtitle && (
          <div
            style={{
              fontSize: 13,
              color: THEME.colors.neutral.light.textSecondary,
              marginTop: 4,
            }}
          >
            {subtitle}
          </div>
        )}
        {/* MOBILE: the run switcher drops below the title as a full-width row.
            DESKTOP: it lives in the right-hand controls cluster instead. */}
        {isMobile && (
          <div style={{ marginTop: THEME.spacing.sm }}>
            <RunSwitcher
              runs={runs}
              current={run}
              onSelect={onSelectRun}
              onOpen={onRefreshRuns}
              fullWidth
            />
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          // On mobile the action buttons sit side by side and split the row
          // width evenly (each flex:1); desktop keeps them at natural size.
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? THEME.spacing.sm : 12,
        }}
      >
        {/* Desktop keeps the run switcher in the controls cluster; on mobile it
            moves up beside the title (rendered in the title row above). */}
        {!isMobile && (
          <RunSwitcher
            runs={runs}
            current={run}
            onSelect={onSelectRun}
            onOpen={onRefreshRuns}
          />
        )}
        {actionError && (
          <span style={{ color: THEME.colors.semantic.danger, fontSize: 13 }}>
            {actionError}
          </span>
        )}
        {run?.status === "completed" && (
          // Recovery path for a run completed too early — unlocks the cells
          // again. Secondary styling keeps Start New Run the primary action.
          <button
            type="button"
            onClick={onReopen}
            disabled={busy}
            style={{ ...secondaryButton(!busy), ...(isMobile ? MOBILE_FULL_BUTTON : {}) }}
          >
            {busy ? "Reopening…" : "Reopen"}
          </button>
        )}
        {/* Persistent "Start New Run" — available on any loaded run (draft or
            completed). It's the primary action on a completed run; on a draft
            it's secondary so "Mark Completed" stays the primary. */}
        {run !== null && (
          <button
            type="button"
            onClick={onStartNew}
            disabled={busy}
            style={{
              ...(run.status === "completed"
                ? brandPrimaryButton(!busy)
                : secondaryButton(!busy)),
              ...(isMobile ? MOBILE_FULL_BUTTON : {}),
            }}
          >
            Start New Run
          </button>
        )}
        <PrimaryAction
          run={run}
          bothUploaded={bothUploaded}
          busy={busy}
          isMobile={isMobile}
          onBuildRun={onBuildRun}
          onMarkCompleted={onMarkCompleted}
        />
      </div>
    </div>
  )
}

interface PrimaryActionProps {
  run: AuctionRunSummary | null
  bothUploaded: boolean
  busy: boolean
  isMobile: boolean
  onBuildRun: () => void
  onMarkCompleted: () => void
}

// The header's state-driven primary button. Its label, handler, and enabled
// state shift with run status:
//   no run  → Build Run      (enabled once both files are uploaded)
//   draft   → Mark Completed
//   done    → nothing (the persistent "Start New Run" button is the action)
function PrimaryAction({
  run,
  bothUploaded,
  busy,
  isMobile,
  onBuildRun,
  onMarkCompleted,
}: PrimaryActionProps) {
  const mobile = isMobile ? MOBILE_FULL_BUTTON : {}
  if (run === null) {
    const enabled = bothUploaded && !busy
    return (
      <button
        type="button"
        onClick={onBuildRun}
        disabled={!enabled}
        style={{ ...brandPrimaryButton(enabled), ...mobile }}
      >
        {busy ? "Building…" : "Build Run"}
      </button>
    )
  }

  if (run.status === "draft") {
    return (
      <button
        type="button"
        onClick={onMarkCompleted}
        disabled={busy}
        style={{ ...brandPrimaryButton(!busy), ...mobile }}
      >
        {busy ? "Marking…" : "Mark Completed"}
      </button>
    )
  }

  // Completed — the persistent "Start New Run" button covers the action.
  return null
}

function buildSubtitle(
  session: AuctionSession | null,
  run: AuctionRunSummary | null,
): string {
  if (run) {
    const vehicles = `${run.vehicle_count} cars`
    const status = run.status === "draft" ? "Draft" : "Completed (read-only)"
    return `${vehicles} · ${status}`
  }
  if (!session?.adesa_run_list || !session?.vauto) {
    const have = (session?.adesa_run_list ? 1 : 0) + (session?.vauto ? 1 : 0)
    return `${have} of 2 files uploaded`
  }
  return `${session.adesa_run_list.row_count.toLocaleString()} run-list rows ready`
}

// Brand primary button — cite components/audit/DownloadMenu.tsx:146.
function brandPrimaryButton(enabled: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 14,
    fontWeight: 500,
    color: THEME.colors.white,
    backgroundColor: THEME.colors.navy,
    border: "none",
    borderRadius: THEME.borderRadius.md,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    userSelect: "none",
    transition: "opacity 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: THEME.spacing.xs,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}

// Secondary button — cite components/photoConfiguration/DeletePlateConfirm.tsx:135
// (secondaryButtonStyle): white surface, silver border, primary text. The quiet
// complement to the navy primary, with the same disabled/opacity treatment.
function secondaryButton(enabled: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 14,
    fontWeight: 500,
    background: THEME.colors.neutral.light.surface,
    color: THEME.colors.neutral.light.textPrimary,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    userSelect: "none",
    transition: "opacity 0.15s",
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}
